import type { LabResultTemplateDocument } from '@type/lab_results.type'
import type {
  LabAnalyteRange,
  LabResultFlag,
  LabTestTemplateDefinitionRow,
  LabTestTemplateSection
} from '@type/labTestTep.type'

/**
 * Lab result flags (lab repository phase 2).
 *
 * Nothing here computes a flag: the backend (`LabResults::Flag`) fills `rows[].flag` and `rows[].reference`
 * when a result is saved, from the patient's sex / age and the analyte's `guideline` range. The browser only
 * renders what the document carries. Colours match the vitals convention (`src/utils/vitals.ts`).
 */

export const LAB_FLAG_CRITICAL_COLOR = '#E53935'
export const LAB_FLAG_ABNORMAL_COLOR = '#FFB800'

export type LabFlagBadgeSpec = {
  /** Mark shown in the Flag column and in summaries */
  label: 'H' | 'L'
  color: string
  critical: boolean
  title: string
}

const BADGES: Record<LabResultFlag, LabFlagBadgeSpec | null> = {
  critical_high: { color: LAB_FLAG_CRITICAL_COLOR, critical: true, label: 'H', title: 'Critical high' },
  critical_low: { color: LAB_FLAG_CRITICAL_COLOR, critical: true, label: 'L', title: 'Critical low' },
  high: { color: LAB_FLAG_ABNORMAL_COLOR, critical: false, label: 'H', title: 'High' },
  low: { color: LAB_FLAG_ABNORMAL_COLOR, critical: false, label: 'L', title: 'Low' },
  normal: null
}

export const isLabResultFlag = (value: unknown): value is LabResultFlag =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(BADGES, value)

/** Badge for a row flag: H / L, red for critical, amber for high / low; nothing for normal, empty or unknown. */
export function labFlagBadge(flag: unknown): LabFlagBadgeSpec | null {
  if (!isLabResultFlag(flag)) return null
  return BADGES[flag]
}

export const isAbnormalLabFlag = (flag: unknown): boolean => labFlagBadge(flag) !== null

/** Plain text of a Quill / HTML cell. */
export function stripHtmlTags(html: unknown): string {
  if (typeof html !== 'string' || !html) return ''
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>\s*<p>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** Result cell value for a `value_type: 'choice'` row, in the same shape Quill would store. */
export const choiceToCellHtml = (choice: string): string => (choice.trim() ? `<p>${escapeHtml(choice.trim())}</p>` : '')

/** The picked choice behind a stored result cell (`<p>Reactive</p>` -> `Reactive`). */
export const choiceFromCellHtml = (html: unknown): string => stripHtmlTags(html)

const formatBound = (n: number | null | undefined) => (n == null ? null : String(n))

function rangeText(range: LabAnalyteRange): string | null {
  if (range.text && range.text.trim()) return range.text.trim()
  const low = formatBound(range.low)
  const high = formatBound(range.high)
  if (low != null && high != null) return `${low} – ${high}`
  if (low != null) return `≥ ${low}`
  if (high != null) return `≤ ${high}`
  return null
}

const isOpenEnded = (range: LabAnalyteRange) => range.age_max_days == null

/**
 * Hint for the Reference column before the first save (the backend resolves the real text for the patient):
 * the open-ended adult range, `low – high`, or `F low – high · M low – high` when the range is by sex.
 */
export function openAdultRangeHint(ranges: LabAnalyteRange[] | undefined | null): string | null {
  if (!Array.isArray(ranges) || !ranges.length) return null
  const adult = ranges.filter(isOpenEnded)
  const pool = adult.length ? adult : ranges
  const any = pool.find(r => r.sex === 'any')
  if (any) return rangeText(any)
  const female = pool.find(r => r.sex === 'f')
  const male = pool.find(r => r.sex === 'm')
  const parts: string[] = []
  const ft = female ? rangeText(female) : null
  const mt = male ? rangeText(male) : null
  if (ft) parts.push(`F ${ft}`)
  if (mt) parts.push(`M ${mt}`)
  return parts.length ? parts.join(' · ') : null
}

export type AbnormalLabRow = { label: string; value: string; mark: 'H' | 'L'; critical: boolean }

/** Row display name: Parameter / Test / Label cell, tags stripped. */
export function rowDisplayName(row: LabTestTemplateDefinitionRow): string {
  return stripHtmlTags(row.parameter) || stripHtmlTags(row.test) || stripHtmlTags(row.label) || row.analyte_code || ''
}

const tableRows = (sections: LabTestTemplateSection[] | undefined): LabTestTemplateDefinitionRow[] =>
  (sections ?? []).flatMap(section =>
    section.presentation === 'table' ? [...section.rows].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)) : []
  )

/** Rows of a saved document the backend flagged outside their range. */
export function abnormalLabRows(doc: Pick<LabResultTemplateDocument, 'sections'> | undefined | null): AbnormalLabRow[] {
  if (!doc) return []
  const out: AbnormalLabRow[] = []
  for (const row of tableRows(doc.sections)) {
    const badge = labFlagBadge(row.flag)
    if (!badge) continue
    out.push({
      critical: badge.critical,
      label: rowDisplayName(row),
      mark: badge.label,
      value: stripHtmlTags(row.result)
    })
  }
  return out
}

/** True when at least one row carries any backend flag (so "no abnormal" is meaningful). */
export function hasLabFlags(doc: Pick<LabResultTemplateDocument, 'sections'> | undefined | null): boolean {
  if (!doc) return false
  return tableRows(doc.sections).some(row => isLabResultFlag(row.flag))
}

/**
 * One-line summary for result lists, e.g. `2 abnormal: Haemoglobin 9.8 L, WBC 15 H`.
 * Returns `null` for documents without flags (facility templates, results saved before the deploy).
 */
export function abnormalLabSummary(
  doc: Pick<LabResultTemplateDocument, 'sections'> | undefined | null,
  options: { max?: number } = {}
): string | null {
  const rows = abnormalLabRows(doc)
  if (!rows.length) return hasLabFlags(doc) ? 'All flagged values within range' : null
  const max = options.max ?? 6
  const shown = rows.slice(0, max).map(r => {
    const value = r.value ? ` ${r.value}` : ''
    return `${r.label}${value} ${r.mark}${r.critical ? ' (critical)' : ''}`
  })
  const more = rows.length > max ? `, +${rows.length - max} more` : ''
  return `${rows.length} abnormal: ${shown.join(', ')}${more}`
}
